import { parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";
import { escapeLikeWildcards } from "@/helpers/sqlPattern";
import { VuetifyValidation } from "@/helpers/Validation";

/**
 * Shape detection and ILIKE-pattern building for the directory search shared by
 * `ShareActionDialog.buildUserSearchWhere` (the `Users` half of the fan-out) and
 * `api.searchCustomers` (the `Customers` half).
 *
 * WHY THIS EXISTS (PR #3838 — Bugbot HIGH "Formatted phones fail directory match", and finding 1
 * of the AI review on that PR). Both halves used to split the query on whitespace and require
 * EVERY word to match via `$and`, each word interpolated LITERALLY into `%…%`. A phone number does
 * not survive that, for two independent reasons:
 *
 *   1. Punctuation. The stored columns hold no parentheses, dots or dashes, so a pattern built from
 *      `(212) 555-0100`, `212.555.0100` or `212-555-0100` cannot match them — even as ONE word.
 *   2. Tokenization. `+1 212 555 0100` becomes four required terms, and `%+1%` cannot match a number
 *      stored without its country code.
 *
 * Punctuation is the general case; tokenization is one instance of it. So the fix is to normalize a
 * phone-shaped query to DIGITS and match it as a single term — restoring what the pre-LSP-15692
 * client-side filter did (`raw.replace(/\D/g, "")` then `phoneDigits.includes(digits)`).
 *
 * STORED FORMATS (verified in contact-api, not assumed):
 *   * `PhoneNumbers.phonenumberraw` — contiguous, no spaces/dashes/parens, sometimes carrying a
 *     leading "+". contact-api's own lookup generates BOTH forms as candidates
 *     (`controllers/customers.ts` getPhoneLookupWhereClause → "6045551234" AND "+16045551234",
 *     pinned by `tests/controllers/customersPhoneLookup.test.ts`). A digits-only pattern matches
 *     either, because the digits are contiguous in both.
 *   * `PhoneNumbers.phoneNumber` — `parsed.formatInternational()`, i.e. "+1 604 555 1234" WITH
 *     spaces (`db/models/phoneNumber.ts` normalizePhone beforeValidate hook).
 *   * `Users.phone` — `DataTypes.STRING`, `defaultValue: ""`, NO hook and NO validator
 *     (`db/models/user.ts`): free-form, stored exactly as a human typed it.
 *
 * Hence two pattern families: `digitPatterns` for the contiguous-digit column, and
 * `literalPatterns` (as-typed plus libphonenumber's national and international renderings) for the
 * free-form and space-separated columns. A caller ORs together whichever apply to its columns.
 *
 * KNOWN RESIDUAL: a `Users.phone` holding a punctuation style we do not render (e.g. "212.555.0100"
 * stored against "212-555-0100" typed) still cannot be matched from the client, because only the
 * server can normalize the COLUMN. Re-verified against contact-api at PR #3838 review round 7, since
 * this is the one gap a reader is most likely to assume has a cheap client-side fix:
 *
 *   * `Users.phone` is the only phone column on that model — `DataTypes.STRING, defaultValue: ""`,
 *     no hook, no validator. There is NO digits-only companion column to compare against instead,
 *     which is the fix this would otherwise have.
 *   * The three `literalPatterns` (as-typed, `formatNational`, `formatInternational`) are the whole
 *     client-side mitigation, and they only cover renderings we can predict. Adding speculative
 *     separator styles would grow the OR list without bounding the problem.
 *   * contact-api already normalizes the COLUMN where it can —
 *     `regexp_replace(coalesce(...), '[^0-9]', '', 'g')` in `search/PostgresSearch.ts` and
 *     `controllers/conversationViews.ts` — so the durable fix is a server-side digits-normalized
 *     predicate for this search too, and it is a contact-api change, not an admin one.
 *
 * Tracked as a follow-up, not silently accepted.
 */

/**
 * The ONE email authority, borrowed rather than re-declared.
 *
 * Merge of develop into PR #3838 (duplicate-fix collision): this module was extracted from
 * ShareInviteField carrying its own `/^[^\s@]+@[^\s@]+\.[^\s@]+$/`, while develop independently
 * migrated BOTH former copies (ShareInviteField's `isEmail` and ShareActionDialog's
 * `EMAIL_PATTERN`) onto `VuetifyValidation.EmailValidation`. Keeping the extraction AND the hand-
 * rolled regex would have re-created the third copy develop had just deleted, and it is the loosest
 * of the three: it accepts a one-character TLD, an underscore in the domain, consecutive/leading/
 * trailing dots in the local part, and a bare dotted-quad. A picker looser than the writer offers an
 * "Invite <email>" row for an address the send path will reject — so the shape test delegates.
 */
const EMAIL_RULE = VuetifyValidation.EmailValidation("Not a valid email address.");
// Same shape rule as ShareInviteField's PHONE_RE, which this module now owns for both callers.
const PHONE_RE = /^[+0-9(][0-9\s()+.-]*$/;
/**
 * The ONE country every search-side phone parse uses.
 *
 * PR #3838 review: this used to be an optional country PARAMETER on
 * phoneSearchCandidates/phoneSearchPatterns. Round 5 unified the match FUNCTION
 * (phoneMatchesCandidates) across the filter, the subtitle and the invite suppression — and then the
 * ARGUMENTS diverged: the filter and both server predicates used the default while the suppression
 * passed the ORG country, so for a non-US org a hit the filter had rendered could fail the
 * suppression and the raw invite row survived beside the matched contact. The parameter is GONE
 * rather than documented, because a shared helper with an optional country argument is precisely what
 * made that divergence representable, and only one caller ever passed it — the buggy one.
 *
 * "US" is not arbitrary: it mirrors SERVER_PHONE_VALIDATION_COUNTRY in helpers/phone.ts, which must
 * match contact-api's PhoneNumber model (which always validates against that same country). Search is
 * therefore parsed on the same basis the server stores on. STAGING/dedup still uses the ORG country
 * via ShareInviteField.phoneKey — a different question ("what number will we text?") with a
 * different correct answer, and it is deliberately not routed through this module.
 *
 * A non-US org typing a NATIONAL-format number remains the KNOWN RESIDUAL documented below: closing
 * it needs the server-side digits-normalized predicate, not a per-call-site country.
 */
const SEARCH_PARSE_COUNTRY: CountryCode = "US";

/**
 * THE definition of "how many characters before a directory search happens at all".
 *
 * The eighth instance of this PR's recurring defect, found while fixing the seventh and fixed the
 * same way. It lived as `MIN_DIRECTORY_SEARCH_LENGTH` in ShareActionDialog AND as
 * `DEFAULT_MIN_SEARCH_LENGTH` in ShareInviteField, whose comment said "Mirrors ShareActionDialog's
 * MIN_DIRECTORY_SEARCH_LENGTH" — and a mirror is exactly the shape that has cost this PR three
 * rounds. They happen to agree today only because the parent binds `minSearchLength` from its own
 * copy; drop that binding, or mount the field anywhere else, and the component silently answers
 * "was this query even searched?" with a different number than the thing doing the searching. The
 * consequence is the H1 regression this prop exists to prevent: a flat "No people match" asserting a
 * negative nothing checked.
 *
 * Lives here rather than in either component because a `<script>`-block const in an SFC cannot be
 * imported, which is why it was copied in the first place.
 */
export const MIN_DIRECTORY_SEARCH_LENGTH = 2;

/**
 * THE definition of "enough digits to name a phone number", for this module and for every consumer
 * of it. One threshold, derived everywhere, never restated.
 *
 * Bugbot MEDIUM at 1bb632e821 (the seventh instance of "two places encoding one rule" on PR #3838,
 * and the third inside a fix for it). This number used to have two rivals, both of them 3, both
 * DISAGREEING with it on the edge:
 *
 *   1. `MIN_ATTRIBUTABLE_PHONE_DIGITS` in ShareInviteField, which gated whether a query's digits may
 *      NAME the number an invite is sent to. At 3 it granted the "explicitly typed number" tier to a
 *      run this very module does not consider a phone number at all — so "Dana 415" outranked a real
 *      address match and texted an area-code sibling. That constant is gone; the call site imports
 *      this one.
 *   2. A bare `candidate.length >= 3` inside `phoneMatchesCandidates`, whose own comment said it
 *      existed "as a guard against MIN_PHONE_DIGITS being lowered" — while hardcoding a number that
 *      would then be HIGHER than the lowered floor, silently making the client stricter than the
 *      server it is supposed to mirror. It now reads this constant, so it cannot invert.
 *
 * Exported deliberately: a consumer that needs to ask "is this run long enough to be a number?" must
 * be able to ask THIS, rather than restate it and drift.
 */
export const MIN_PHONE_DIGITS = 4;
/**
 * E.164 allows at most 15 digits; a little headroom covers an extension. Break-it round 1 on this
 * delta: a 31-digit string was being accepted as phone-shaped purely because it fell under the
 * CHARACTER ceiling below, so a digit ceiling is needed as well.
 */
const MAX_PHONE_DIGITS = 20;
/**
 * A trailing extension ("x123", "ext 123", "ext. 123", "#123"). Recognised for SEARCH only — see
 * isPhoneSearchQuery — because libphonenumber parses it and it must not defeat the digit lookup.
 */
const PHONE_EXTENSION_RE = /^(.*?)\s*(?:x|ext\.?|#)\s*(\d{1,6})$/i;
/**
 * Crash guard before anything reaches libphonenumber-js, in the same spirit as `formatPhone`'s
 * length bail-out in `helpers/phone.ts` (LSP-16692) — but deliberately STRICTER, and NOT a mirror of
 * it. `formatPhone` uses `FIELD_LENGTHS.PHONE_INPUT` (250, matching libphonenumber's own
 * MAX_INPUT_STRING_LENGTH) because it must render whatever a phone FIELD holds. This is a SEARCH-BOX
 * guard: no phone anyone types to find a contact is longer than this, and a lower ceiling also
 * bounds the literal patterns emitted below. (A PR #3838 review round corrected an earlier note
 * here that described 32 as mirroring formatPhone's ceiling.)
 */
const MAX_PHONE_QUERY_LENGTH = 32;

export function isEmailQuery(value: string): boolean {
	const trimmed = (value ?? "").trim();
	// The emptiness guard is load-bearing and must come FIRST: EmailValidation is a Vuetify field
	// rule, so it reports an EMPTY value as valid (a blank optional field is not an error). Calling
	// it bare would make isEmailQuery("") true, which would in turn make isPhoneQuery bail on an
	// empty query via its email exclusion rather than via its own !trimmed guard.
	return !!trimmed && EMAIL_RULE.isValid(trimmed) === true;
}

/**
 * Phone-SHAPED, deliberately not phone-VALID: the search should still normalize a partial number.
 * Email is excluded FIRST, so plus-addressing (`a+b@x.com`) is never read as a phone — and `@`
 * fails PHONE_RE anyway. A name is excluded by PHONE_RE's leading-character class, so `Anne-Marie`
 * keeps its per-word behavior.
 */
export function isPhoneQuery(value: string): boolean {
	const trimmed = (value ?? "").trim();
	if (!trimmed || trimmed.length > MAX_PHONE_QUERY_LENGTH) return false;
	if (isEmailQuery(trimmed)) return false;
	if (!PHONE_RE.test(trimmed)) return false;
	const digitCount = trimmed.replace(/\D/g, "").length;
	return digitCount >= MIN_PHONE_DIGITS && digitCount <= MAX_PHONE_DIGITS;
}

/**
 * The SEARCH-side shape test: `isPhoneQuery`, plus a trailing extension.
 *
 * Deliberately NOT the same predicate as `isPhoneQuery`, which also gates STAGING in
 * ShareInviteField (the raw "Invite <number>" row). Break-it round 1 on this delta found that
 * widening the staging predicate to accept "212-555-0100 x123" would offer that whole string as a
 * raw phone invite and stage it as the recipient's number — texting a malformed number. So the
 * search is permissive (find the contact by its base digits) while staging stays strict (refuse to
 * treat an extension-bearing string as a dialable number).
 *
 * The overall-length ceiling (MAX_PHONE_QUERY_LENGTH — a search-box guard, not formatPhone's 250)
 * is re-applied here (break-it round 2): checking it only against the extension-stripped BASE let an
 * arbitrarily long run of whitespace before the extension through, and that whole string became an
 * unbounded literal pattern.
 */
export function isPhoneSearchQuery(value: string): boolean {
	const trimmed = (value ?? "").trim();
	if (!trimmed || trimmed.length > MAX_PHONE_QUERY_LENGTH) return false;
	if (isPhoneQuery(trimmed)) return true;
	const withoutExtension = trimmed.match(PHONE_EXTENSION_RE);
	return !!withoutExtension && isPhoneQuery(withoutExtension[1]);
}

export interface PhoneSearchCandidates {
	/** Unescaped digit strings, for columns holding contiguous digits (PhoneNumbers.phonenumberraw). */
	readonly digits: readonly string[];
	/** Unescaped literal renderings, for free-form / space-separated columns. */
	readonly literals: readonly string[];
}

/**
 * The digit and literal candidates a phone-shaped query should be matched by, or `null` when the
 * query is not phone-shaped. Exported UNESCAPED so the client-side directoryHits filter can compare
 * against a row's digit-stripped phone.
 *
 * Precisely what client and server share (a PR #3838 review round corrected an earlier note here
 * that claimed "exactly the candidate set the server predicate uses"): the client tests
 * `digits` only, while the server predicates test the phone column against BOTH `digits` and
 * `literals`. That is sufficient — and not a coincidence — because every literal is a RENDERING of
 * the same base number, so its digit-stripped form always contains a member of `digits`. A literal
 * is therefore never able to match a row the client would reject. If a future edit adds a literal
 * that is NOT a pure rendering of the base digits (an org-specific display format, a spelled-out
 * number), that guarantee breaks and the client filter must start consulting `literals` too.
 */
export function phoneSearchCandidates(query: string): PhoneSearchCandidates | null {
	const trimmed = (query ?? "").trim();
	if (!isPhoneSearchQuery(trimmed)) return null;

	// libphonenumber-js throws NOT_A_NUMBER on a tab or newline INSIDE the number, which a paste out
	// of a spreadsheet or email easily carries. Whitespace is pure formatting noise in a phone, so
	// collapse it before parsing — otherwise every parse-derived candidate is lost for exactly the
	// paste this fix targets.
	const collapsed = trimmed.replace(/\s+/g, " ");
	const extension = collapsed.match(PHONE_EXTENSION_RE);
	// Break-it round 2: the BASE number is the only thing that may contribute a bare digit
	// candidate. Adding the digits of the whole string when an extension is present both invented a
	// number nobody stores ("2125550100" + "123") — which can match an unrelated longer row — and
	// smuggled a 26-digit pattern past the 20-digit ceiling.
	const base = extension ? extension[1].trim() : collapsed;

	const digits = new Set<string>();
	const literals = new Set<string>([trimmed, collapsed]);
	const baseDigits = base.replace(/\D/g, "");
	if (baseDigits.length >= MIN_PHONE_DIGITS) digits.add(baseDigits);
	if (extension) literals.add(base);

	const addParsed = (input: string, country?: CountryCode) => {
		try {
			const parsed = country ? parsePhoneNumberWithError(input, { defaultCountry: country }) : parsePhoneNumberWithError(input);
			if (!parsed.isPossible()) return;
			// nationalNumber drops the country code, which is what a nationally-stored row holds.
			for (const candidate of [parsed.format("E.164").replace(/\D/g, ""), String(parsed.nationalNumber)]) {
				if (candidate.length >= MIN_PHONE_DIGITS && candidate.length <= MAX_PHONE_DIGITS) digits.add(candidate);
			}
			// The renderings a human most often types into a free-form field, and exactly what
			// contact-api stores in PhoneNumbers.phoneNumber (formatInternational).
			literals.add(parsed.formatInternational());
			literals.add(parsed.formatNational());
		} catch {
			// Unparseable in this form; other candidates still apply.
		}
	};

	// Parse the BASE, not the extension-bearing string: with an extension present,
	// formatNational()/formatInternational() RENDER it ("(212) 555-0100 ext. 123"), which cannot
	// match a column holding just the base number (break-it round 1).
	addParsed(base, SEARCH_PARSE_COUNTRY);

	// "00" is the international access prefix across most of the world, so a pasted
	// "0044 20 7946 0958" must still find a number stored as "+442079460958". Only a SUCCESSFUL
	// reparse contributes candidates — break-it round 2: adding the stripped digits unconditionally
	// turned "0000000" into a bare "%00000%" that falsely matched shorter numbers.
	if (baseDigits.startsWith("00") && baseDigits.length > MIN_PHONE_DIGITS + 2) {
		addParsed(`+${baseDigits.slice(2)}`);
	}

	return { digits: [...digits], literals: [...literals] };
}

/**
 * Whether a directory row's phone matches a typed query's candidates.
 *
 * The single notion of "this row IS the number that was typed", shared by everything that needs to
 * ask it: the hit filter, the hit subtitle, and inviteRow's suppression of the redundant raw
 * "Invite <number>" row (round-5 finding 1). Those three drifting apart is what produced both the
 * dropped-row bug and the duplicate-invite bug, so they must not each carry their own copy.
 *
 * The length floor is MIN_PHONE_DIGITS — the module's one definition — not a restatement of it.
 * It is currently UNREACHABLE, because every candidate is built by phoneSearchCandidates under that
 * same floor; the helper spec pins exactly that, which is also the parity proof that this check can
 * never reject a candidate and so can never make the client stricter than the server predicates.
 * Kept as a cheap assertion of the invariant rather than deleted, and reading the constant means it
 * stays a no-op if the floor ever moves, in EITHER direction. It used to hardcode 3, which would
 * have become a hidden stricter-than-the-server floor the moment MIN_PHONE_DIGITS dropped below it.
 */
export function phoneMatchesCandidates(rowPhone: string | undefined | null, candidates: PhoneSearchCandidates | null): boolean {
	if (!candidates || !rowPhone) return false;
	const rowDigits = rowPhone.replace(/\D/g, "");
	if (!rowDigits) return false;
	return candidates.digits.some((candidate) => candidate.length >= MIN_PHONE_DIGITS && rowDigits.includes(candidate));
}

export interface PhoneSearchPatterns {
	/** For columns holding contiguous digits (optionally "+"-prefixed): PhoneNumbers.phonenumberraw. */
	readonly digitPatterns: readonly string[];
	/** For free-form or space-separated columns: Users.phone, PhoneNumbers.phoneNumber. */
	readonly literalPatterns: readonly string[];
}

/**
 * ILIKE patterns for a phone-shaped query, or `null` when the query is not phone-shaped (callers
 * then keep the per-word `$and` behavior). Every returned pattern is already wildcard-escaped.
 */
export function phoneSearchPatterns(query: string): PhoneSearchPatterns | null {
	const candidates = phoneSearchCandidates(query);
	if (!candidates) return null;
	return {
		digitPatterns: candidates.digits.map((value) => `%${escapeLikeWildcards(value)}%`),
		literalPatterns: candidates.literals.map((value) => `%${escapeLikeWildcards(value)}%`)
	};
}

/** Per-word tokens for a non-phone query. Splits on ANY whitespace, deduped, already escaped. */
export function textSearchPatterns(query: string): string[] {
	const words = new Set((query ?? "").split(/\s+/).filter((word) => word !== ""));
	return [...words].map((word) => `%${escapeLikeWildcards(word)}%`);
}
